import { useMutation, useQuery } from '@apollo/client';
import { getNotificationTemplate } from '../../../graph/notificationTemplates';
import { notifyReadyForSurgeon } from '../../../graph/patients';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import NotificationsIcon from '@material-ui/icons/Notifications';
import Tooltip from '../../Tooltip';
import React, { useRef, useState } from 'react';
import { fontSizeIconSide, SideOption } from './pacuAndPrep/PatientOptions';
import { useTheme } from '@material-ui/core/styles';
import { getLogEntries, isPatientReadyForSurgeonNotifyFn } from './tablet/utils';
import Physician from '../../../types/Physician';
import { Patient } from '../../../types/Patient';
import { CustomModal } from './tablet/OperationRoomTabletV1';
import usePhysicianPushEnabled from '../../../hooks/usePhysicianPushEnabled';
import { SendingSetting } from '../../entities/notificationTemplate/NotificationTemplates';

const NotifyPhysicianModal = CustomModal(
  'This text message will be sent to the physician when his/her patient is ready to be seen in PreOp.'
);

const PushNotifyPhysicianModal = CustomModal(
  'Notify the physician that the patient is ready in PreOp. A mobile app notification will be requested.'
);

const PushAndSmsNotifyPhysicianModal = CustomModal(
  'Notify the physician that the patient is ready in PreOp. A mobile app notification and the text message below will be requested.'
);

export const NotifyButton = ({
  notifiedLabel,
  notNotifiedLabel,
  isNotified,
  notifyEnabled,
  pushEnabled = false,
  message,
  notify,
}: {
  notifiedLabel: string;
  notNotifiedLabel: string;
  isNotified: boolean;
  message?: string;
  notify: () => Promise<void>;
  notifyEnabled: boolean;
  pushEnabled?: boolean;
}) => {
  const theme = useTheme();
  const ConfirmationModal = pushEnabled
    ? message
      ? PushAndSmsNotifyPhysicianModal
      : PushNotifyPhysicianModal
    : NotifyPhysicianModal;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [working, setWorking] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const inFlight = useRef(false);

  const openModal = () => {
    if (!isNotified && notifyEnabled) {
      setErrorMessage('');
      setIsModalOpen(true);
    }
  };
  const handleConfirm = async () => {
    if (inFlight.current || isNotified || !notifyEnabled) return;
    inFlight.current = true;
    setWorking(true);
    setErrorMessage('');
    try {
      await notify();
      setIsModalOpen(false);
    } catch {
      setErrorMessage('Unable to confirm the notification. Check the patient status before trying again.');
    } finally {
      inFlight.current = false;
      setWorking(false);
    }
  };
  return (
    <>
      <SideOption
        smsIndicator={false}
        icon={
          isNotified ? (
            <CheckCircleIcon
              style={{
                opacity: '0.7',
                fontSize: fontSizeIconSide,
                color: theme.palette.success.main,
              }}
            />
          ) : (
            <NotificationsIcon
              color={!notifyEnabled ? 'disabled' : 'secondary'}
              style={{
                fontSize: fontSizeIconSide,
              }}
            />
          )
        }
        label={isNotified ? notifiedLabel : notNotifiedLabel}
        disabled={!notifyEnabled || isNotified}
        onClick={openModal}
      />
      {isModalOpen && (
        <ConfirmationModal
          handleCancel={() => setIsModalOpen(false)}
          handleConfirm={handleConfirm}
          working={working}
          errorMessage={errorMessage}
          messageToSend={message}
        />
      )}
    </>
  );
};

const PhysicianNotifyButton = ({ patient, physician }: { patient?: Patient; physician?: Physician }) => {
  const pushEnabled = usePhysicianPushEnabled(Boolean(patient?.id && physician?.id));
  const patientLogs = getLogEntries(patient);
  const { data: template } = useQuery(getNotificationTemplate, {
    variables: { trigger: 'ReadyForSurgeonNotify' },
  });

  const [notifyPhysician] = useMutation(notifyReadyForSurgeon, {
    variables: {
      id: patient?.id,
    },
  });

  const notify = async () => {
    await notifyPhysician();
  };

  const isSurgeonNotified = isPatientReadyForSurgeonNotifyFn(patientLogs);
  const readyForSurgeonNotification = template?.getNotificationTemplate?.[0] || null;
  const shouldSendNotification = physician?.shouldSendNotification || null;
  const hasPhoneNumber = physician?.notificationNumbers || null;

  const messageContent = readyForSurgeonNotification?.content;
  const smsEnabled = Boolean(
    readyForSurgeonNotification?.sendingSetting !== SendingSetting.Off && hasPhoneNumber && shouldSendNotification
  );
  const notifyPhysicianEnabled = smsEnabled || pushEnabled;
  const notifyPhysicianTooltip = pushEnabled
    ? null
    : readyForSurgeonNotification?.sendingSetting === SendingSetting.Off
      ? 'Physician messaging has been turned off. Go to message configuration to enable it.'
      : !shouldSendNotification
        ? 'Physician messaging has been turned off. Physician doesn’t want to receive notification.'
        : !hasPhoneNumber
          ? 'Physician does not have a phone number.'
          : null;

  return (
    <Tooltip delay={0} content={notifyPhysicianTooltip}>
      <NotifyButton
        isNotified={isSurgeonNotified}
        notifyEnabled={Boolean(notifyPhysicianEnabled)}
        notNotifiedLabel={'Notify Physician'}
        notifiedLabel={'Physician Notified'}
        message={smsEnabled ? messageContent : undefined}
        pushEnabled={pushEnabled}
        notify={notify}
      />
    </Tooltip>
  );
};

export default PhysicianNotifyButton;
